import {
  Checkbox as MuiCheckbox,
  FormControlLabel as MuiFormControlLabel,
  useTheme,
} from '@mui/material';
import Accordion from 'components/Accordion';
import LoadingPage from 'components/LoadingPage';
import { Search } from 'components/ui';
import useGetValueset from 'lib/hooks/useGetValueset';
import { mergeAndSortArrays } from 'lib/utils/returnUniqueIdValues';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import InfiniteScroll from 'react-infinite-scroll-component';

export default function ConditionFilter({ filters, setFilters, defaultExpanded = false }) {
  const { t } = useTranslation();
  const theme = useTheme()
  const currentLanguage = useSelector(
    (state) => state.user.user.selectedLanguage
  );

  const listContainerRef = useRef(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const { valueset, loading, loadMore } = useGetValueset({
    url: 'https://www.alakinhealth.com/fhir/ValueSet/icd_10',
    language: currentLanguage || 'en',
    filter: {
      text: debouncedSearch,
    },
  });
  
  useEffect(() => {
    const handleScroll = (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        loadMore();
      }
    };

    const listDiv = listContainerRef.current;
    if (listDiv) {
      listDiv.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (listDiv) {
        listDiv.removeEventListener('scroll', handleScroll);
      }
    };
  }, [loadMore]);

  const handleCheckboxClick = (value) => {
    const isPresent = !!filters.condition?.find(
      (item) => value.code === item.code
    );

    const updatedConditionsList = isPresent
      ? filters.condition?.filter((item) => value.code !== item.code)
      : [...filters.condition, value];
    setFilters((curr) => ({
      ...curr,
      condition: updatedConditionsList,
    }));
  };

  return (
    <Accordion title={t('Conditions')} defaultExpanded={defaultExpanded}>
      <Search
        small
        placeholder={t('Search conditions')}
        onChange={(e, v) => setSearch(e.target.value)}
      />

      <div
        ref={listContainerRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 10,
          margin: '10px 0',
          border: `1px solid ${theme.palette.colors.gray400}`,
          borderRadius: 6,
          maxHeight: '450px',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <LoadingPage style={{ width: 100, height: 100 }} />
        ) : (
          mergeAndSortArrays(filters.condition, valueset || [])?.map?.(
            (condition) => {
              return (
                <MuiFormControlLabel
                  key={condition.code}
                  control={
                    <MuiCheckbox
                      checked={
                        !!filters.condition.find(
                          (item) => item.code === condition.code
                        )
                      }
                      onClick={() => handleCheckboxClick(condition)}
                    />
                  }
                  label={condition.display}
                />
              );
            }
          )
        )}
      </div>
    </Accordion>
  );
}
